import { useState, type ReactNode } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import { useAccess, useCanConfigure, useHomes, useModel } from '../lib/hooks.js';
import { Icon, roomIcon } from '../lib/icons.js';
import { indexModel } from '../lib/model.js';
import { spacesIndex } from '../lib/spaces.js';
import { getPref, lastHome, setPref } from '../lib/prefs.js';
import { IconButton } from '../components/ui/index.js';
import { MODULES, isDashboard } from './modules.js';
import { AlarmShield } from './Alarm.js';
import { SidebarWarnings } from './Warnings.js';

/** Desktop navigation column (≥1024px). Phones and tablets get the same content in NavDrawer. */
export function Sidebar(): ReactNode {
  return (
    <nav className="sidebar glass" aria-label="Navigation">
      <SidebarContent />
    </nav>
  );
}

/**
 * Brand, home switcher, the current home's pages and the account footer.
 * `onClose` adds a close button to the brand row (drawer use).
 */
export function SidebarContent({ onClose }: { onClose?: () => void }): ReactNode {
  const homes = useHomes();
  const match = useMatch('/h/:home/*');
  const home = match?.params.home ?? lastHome.get() ?? homes[0]?.id ?? '';
  const model = useModel(home);
  const access = useAccess(home);
  const canConfigure = useCanConfigure(home);
  // Child spaces (ensuites) live under their parent room, so the nav lists home-level rooms only.
  const idx = model ? { ...indexModel(model), floors: spacesIndex(model).homeFloors } : null;
  const navigate = useNavigate();
  const [collapsed, toggleFloor] = useCollapsedFloors(home);
  const dashboards = model && access ? access.modules.filter((m) => model.modules.includes(m) && MODULES[m] && isDashboard(m)) : [];
  return (
    <>
      <div className="sidebar-brand">
        <img src="/favicon.svg" alt="" width={28} height={28} />
        <span>Homes</span>
        {onClose && (
          <>
            <span className="spacer" />
            {home && <AlarmShield home={home} size={34} />}
            <IconButton icon="close" label="Close menu" size={34} onClick={onClose} data-testid="drawer-close" />
          </>
        )}
      </div>
      {homes.length > 1 && (
        <div className="sidebar-homes" role="tablist" aria-label="Homes">
          {homes.map((h, i) => (
            <button
              key={h.id}
              type="button"
              role="tab"
              aria-selected={h.id === home}
              className="sidebar-home"
              onClick={() => navigate(`/h/${h.id}`)}
              title={`${h.name} (⌘${i + 1})`}
              data-testid={`side-home-${h.id}`}
            >
              <span className="dot" data-tone={h.online ? 'positive' : 'alert'} />
              <span>{h.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className="sidebar-scroll">
        {home && (
          <NavLink to={`/h/${home}`} end className="side-link">
            <Icon name="home" size={17} /> Home
          </NavLink>
        )}
        {home && model?.modules.includes('music') && access?.modules.includes('music') && (
          <NavLink to={`/h/${home}/music`} className="side-link" data-testid="nav-music">
            <Icon name="music" size={16} /> Music
          </NavLink>
        )}
        {home && model?.modules.includes('calendar') && access?.modules.includes('calendar') && (
          <NavLink to={`/h/${home}/calendar`} className="side-link" data-testid="nav-calendar">
            <Icon name="calendar" size={16} /> Calendar
          </NavLink>
        )}
        {dashboards.length > 0 && (
          <div className="side-group">
            <span className="side-label">Dashboards</span>
            {dashboards.map((m) => (
              <NavLink key={m} to={`/h/${home}/dashboards/${m}`} className="side-link">
                <Icon name={MODULES[m]!.icon} size={16} /> {MODULES[m]!.name}
              </NavLink>
            ))}
          </div>
        )}
        {idx?.floors.map(({ floor, rooms }) => {
          const key = floor?.id ?? 'none';
          const open = !collapsed.has(key);
          const here = match?.params['*']?.startsWith('room/') ? match.params['*'].slice(5).split('/')[0] : undefined;
          // Collapsed floors still show the room you're in, so the current page never vanishes from the nav.
          const shown = open ? rooms : rooms.filter((r) => r.id === here);
          return (
            <div key={key} className="side-group" data-collapsed={open ? undefined : ''}>
              <button
                type="button"
                className="side-label side-toggle"
                aria-expanded={open}
                onClick={() => toggleFloor(key)}
                data-testid={`side-floor-${key}`}
              >
                <span>{floor?.name ?? 'Rooms'}</span>
                {!open && <span className="side-count">{rooms.length}</span>}
                <Icon name="chevron-down" size={13} strokeWidth={2.2} className="side-chevron" />
              </button>
              {shown.map((r) => (
                <NavLink key={r.id} to={`/h/${home}/room/${r.id}`} className="side-link">
                  <Icon name={roomIcon(r)} size={16} /> {r.name}
                </NavLink>
              ))}
            </div>
          );
        })}
        {home && (canConfigure || access?.modules.includes('house')) && (
          <div className="side-group">
            <NavLink to={`/h/${home}/settings`} className="side-link" data-testid="nav-settings">
              <Icon name="sliders" size={16} /> Settings
            </NavLink>
          </div>
        )}
        {access?.isAdmin && (
          <div className="side-group">
            <span className="side-label">Admin</span>
            <NavLink to={`/h/${home}/unsorted`} className="side-link">
              <Icon name="inbox" size={16} /> Unsorted
              {idx && idx.unsorted.length > 0 && <span className="side-count">{idx.unsorted.length}</span>}
            </NavLink>
            <NavLink to={`/h/${home}/admin`} className="side-link">
              <Icon name="users" size={16} /> People & access
            </NavLink>
          </div>
        )}
        {home && <SidebarWarnings home={home} {...(onClose ? { onNavigate: onClose } : {})} />}
      </div>
      <div className="sidebar-foot">
        {homes.length > 1 && (
          <NavLink to="/overview" className="side-link">
            <Icon name="grid" size={16} /> Overview
          </NavLink>
        )}
        <NavLink to="/settings" className="side-link">
          <Icon name="user" size={16} /> Account
        </NavLink>
      </div>
    </>
  );
}

/** Floor groups the user folded away in the nav, remembered per home (localStorage `homes.nav-collapsed.<home>`). */
function useCollapsedFloors(home: string): [Set<string>, (floor: string) => void] {
  const key = `nav-collapsed.${home}`;
  const read = (): Set<string> => {
    try {
      const v: unknown = JSON.parse(getPref(key) ?? '[]');
      return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
    } catch {
      return new Set();
    }
  };
  const [state, setState] = useState(() => ({ home, set: read() }));
  // Switching homes reads that home's folds (state is keyed by home, so no effect needed).
  const current = state.home === home ? state.set : read();
  if (state.home !== home) setState({ home, set: current });
  const toggle = (floor: string): void => {
    const next = new Set(current);
    if (next.has(floor)) next.delete(floor);
    else next.add(floor);
    setPref(key, next.size ? JSON.stringify([...next]) : null);
    setState({ home, set: next });
  };
  return [current, toggle];
}
